import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux'

import { CurrentFormUrlPathContext } from '../../AppRoot/hooks/useAppRootLogic'
import { AppDispatch, AppState, apphistory } from '../../../store'
import { AppLinks } from '../../../environment/app-links'
import { Environment } from '../../../environment/app-settings'
import { createAndStartHub } from '../../../store/middleware/credential.action'
import { SET_APP_HAS_ERROR } from '../../../store/actions/application-actions'
import useAppInitLogic from '../../../hooks/useAppInitLogic'
import { APP_INIT_TYPES } from '../../../hooks/HooksProps'
import { APP_SOURCE_TYPES } from '../../../store/types'
import analyticsService from '../../../service/analytics.service'
import { SessionStorage } from '../../../common/cookie-storage'
import { STORAGEKEYS } from '../../../common/keys'


/**
 * Custom hook obsługujący logikę strony z powrotem klienta do aplikacji 
*/
const useComebackPageLogic = () => {
   const dispatch = useDispatch<AppDispatch>()
   const { setCurrentUrlFormPath } = useContext(CurrentFormUrlPathContext)
   const { getAccessToken, getAppConfiguration, handleAnalyticsRequests } = useAppInitLogic(APP_INIT_TYPES.COMEBACK_PAGE)
   const currentApplicationStatus = useSelector((state: AppState) => state.applicationState.applicationStatus)
   const ANALYTICS_EVENT_TYPES_NAMES = useSelector((state: AppState) => state.lenConfigState.analyticsEventTypesNames)

   const getComebackSearchParams = (): URLSearchParams => {
      if (typeof window === "undefined") {
         return new URLSearchParams()
      }

      return new URLSearchParams(window.location.search)
   }

   const getKontomatikBmVerifyTarget = (searchParams: URLSearchParams): string => {
      const kontomatikSearchParams = new URLSearchParams()
      const redirectionId = searchParams.get("redirectionId") ?? searchParams.get("returnId")
      const error = searchParams.get("error")
      const errorCode = searchParams.get("errorCode")
      const status = searchParams.get("status")

      if (redirectionId) {
         kontomatikSearchParams.set("redirectionId", redirectionId)
      }
      if (error) {
         kontomatikSearchParams.set("error", error)
      }
      if (errorCode) {
         kontomatikSearchParams.set("errorCode", errorCode)
      }
      if (status) {
         kontomatikSearchParams.set("status", status)
      }

      return kontomatikSearchParams.toString()
         ? `${AppLinks.bmverify}?${kontomatikSearchParams.toString()}`
         : AppLinks.bmverify
   }

   const redirectToKontomatikBankVerification = (searchParams: URLSearchParams): void => {
      const bmVerifyTarget = getKontomatikBmVerifyTarget(searchParams)
      setCurrentUrlFormPath(AppLinks.bmverify)
      apphistory.push(bmVerifyTarget)
   }

   const hasRestorableAuthSession = (): boolean => {
      const storage = new SessionStorage()
      const hasUserSession = Boolean(storage.get(STORAGEKEYS.USER_SESSION))
      const hasAccessToken = Boolean(storage.get(STORAGEKEYS.ACCESS_TOKEN))
      return hasUserSession && hasAccessToken
   }

   useEffect(()=> { //Dopiero gdy wszystkie teksty z Backendu zostaną załadowane "puszczamy" proces dalej
      (async () => {
         try {
            const searchParams = getComebackSearchParams()
            const isKontomatikComeback = searchParams.get("source") === APP_INIT_TYPES.KONTOMATIK
            if (!(isKontomatikComeback && hasRestorableAuthSession())) {
               await getAccessToken()
            }
            await getAppConfiguration()                
            await handleAnalyticsRequests()

            if (isKontomatikComeback) {
               // a po zakończeniu (success/fail) kierujemy klienta na ekran wyboru banku.
               createAndStartHub(dispatch, APP_SOURCE_TYPES.COMEBACK_PAGE)
                  .then(() => redirectToKontomatikBankVerification(searchParams))
                  .catch(() => redirectToKontomatikBankVerification(searchParams))
               return
            }

            // ================ POŁĄCZENIE Z HUBEM ================
            createAndStartHub(dispatch, APP_SOURCE_TYPES.COMEBACK_PAGE)
            // ====================================================
         }
         catch (e: any) {
            const ERROR_ANALYTICS_DATA = {
               id: analyticsService.DISPLAY_ERROR_PAGE_IDS.COMEBACK_PAGE_ERROR,
               reason: "Błąd podczas pobierania tokena lub konfiguracji aplikacji, przy powrocie klienta za pomocą linku powrotnego (/return?).",
               details: {
                  description: e.message,
                  errorDataFromBackend: e?.data,
                  additionalData: {
                     applicationStatus: currentApplicationStatus,
                  }
               },
            }
            
            dispatch({ type: SET_APP_HAS_ERROR }) //Ustawiamy globalny błąd aplikacji co spowoduje przejście na ekran z błędem
            await analyticsService.createSingleAnalyticEvent(ANALYTICS_EVENT_TYPES_NAMES?.FrontendErrorPageDisplayed, JSON.stringify(ERROR_ANALYTICS_DATA, undefined, 3))
            
            return 
         }
      })()

   // eslint-disable-next-line react-hooks/exhaustive-deps
   },[])

   useEffect(() => {
      const searchParams = getComebackSearchParams()
      if (searchParams.get("source") === APP_INIT_TYPES.KONTOMATIK) {
         return
      }

      // Status .NEW wymaga wyjątkowej obsługi. Przekierowanie na wszystkie inne statusy 
      // odbywa się po "zalogowaniu" do Backendu z pomocą HUB'a.
      if(currentApplicationStatus === Environment.applicationStatuses.NEW){
         setCurrentUrlFormPath(AppLinks.basicInfoForm)
         apphistory.push(AppLinks.basicInfoForm)
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps   
   }, [ currentApplicationStatus ])
}

export default useComebackPageLogic